import { useState, useEffect, useCallback } from 'react'
import type {
  AgentConfigListItem,
  AgentConfigUpdate,
  ModelRegistryItem,
  ModelInfo,
} from '../../lib/api'
import { Modal } from '../ui/Modal'
import { useModels, useUpdateAgentConfig } from '../../hooks/useAdminConfig'

const TASK_TYPES = [
  { value: 'creative_writing', label: 'Creative writing' },
  { value: 'analysis', label: 'Analysis' },
  { value: 'critique', label: 'Critique' },
  { value: 'summarization', label: 'Summarization' },
  { value: 'code_generation', label: 'Code generation' },
  { value: 'structured_output', label: 'Structured output' },
  { value: 'fast_response', label: 'Fast response' },
  { value: 'web_research', label: 'Web research' },
  { value: 'deep_research', label: 'Deep research' },
  { value: 'reasoning', label: 'Reasoning' },
] as const

const THINKING_LEVELS = [
  { value: '', label: '—' },
  { value: 'minimal', label: 'Minimal' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
] as const

function isModelRegistryItem(m: ModelRegistryItem | ModelInfo): m is ModelRegistryItem {
  return 'model_type' in m
}

function getTextModels(models: (ModelRegistryItem | ModelInfo)[]): (ModelRegistryItem | ModelInfo)[] {
  if (!models.length) return []
  return models.filter((m) => {
    if (isModelRegistryItem(m)) return m.model_type !== 'image'
    return true
  })
}

function getSupportsThinking(modelId: string | null, models: (ModelRegistryItem | ModelInfo)[]): boolean {
  if (!modelId) return false
  const m = models.find((x) => (isModelRegistryItem(x) ? x.model_id ?? x.id : (x as ModelInfo).model_id) === modelId)
  if (!m || !isModelRegistryItem(m)) return false
  return Boolean(m.supports_thinking_level)
}

function getModelDisplayName(m: ModelRegistryItem | ModelInfo): string {
  if (isModelRegistryItem(m)) return m.name ?? m.model_id ?? String(m.id ?? '')
  const mi = m as ModelInfo
  return mi.name ?? mi.model_id
}

function getModelId(m: ModelRegistryItem | ModelInfo): string {
  if (isModelRegistryItem(m)) return m.model_id ?? m.id ?? ''
  return (m as ModelInfo).model_id
}

function listItemToFormState(item: AgentConfigListItem) {
  const d = item.default as Record<string, unknown>
  const o = item.override as Record<string, unknown> | null
  const merged = { ...d, ...(o ?? {}) }
  return {
    system_prompt: (merged.system_prompt as string) ?? '',
    model_override: (merged.model_override as string) ?? null,
    task_type: (merged.task_type as string) ?? '',
    temperature: Number(merged.temperature) || 0.7,
    max_tokens: Number(merged.max_tokens) || 4096,
    max_retries: Number(merged.max_retries) ?? 3,
    timeout_seconds: Number(merged.timeout_seconds) ?? 120,
    thinking_level: (merged.thinking_level as string) ?? '',
    top_p: merged.top_p != null ? Number(merged.top_p) : undefined as number | undefined,
  }
}

export interface AgentEditModalProps {
  isOpen: boolean
  onClose: () => void
  agent: AgentConfigListItem | null
  orgId: string
  onSaved?: () => void
}

export function AgentEditModal({ isOpen, onClose, agent, orgId, onSaved }: AgentEditModalProps) {
  const { data: modelsRaw = [] } = useModels()
  const models = Array.isArray(modelsRaw) ? modelsRaw : []
  const textModels = getTextModels(models)
  const updateAgent = useUpdateAgentConfig()

  const [systemPrompt, setSystemPrompt] = useState('')
  const [modelOverride, setModelOverride] = useState<string | null>(null)
  const [taskType, setTaskType] = useState('')
  const [temperature, setTemperature] = useState(0.7)
  const [maxTokens, setMaxTokens] = useState(4096)
  const [maxRetries, setMaxRetries] = useState(3)
  const [timeoutSeconds, setTimeoutSeconds] = useState(120)
  const [thinkingLevel, setThinkingLevel] = useState('')
  const [topP, setTopP] = useState<number | undefined>(undefined)

  const syncFromAgent = useCallback((item: AgentConfigListItem) => {
    const s = listItemToFormState(item)
    setSystemPrompt(s.system_prompt)
    setModelOverride(s.model_override)
    setTaskType(s.task_type)
    setTemperature(s.temperature)
    setMaxTokens(s.max_tokens)
    setMaxRetries(s.max_retries)
    setTimeoutSeconds(s.timeout_seconds)
    setThinkingLevel(s.thinking_level)
    setTopP(s.top_p)
  }, [])

  useEffect(() => {
    if (agent) syncFromAgent(agent)
  }, [agent, syncFromAgent])

  const showThinkingLevel = getSupportsThinking(modelOverride ?? null, models)
  const busy = updateAgent.isPending

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!agent) return
    const data: AgentConfigUpdate = {
      system_prompt: systemPrompt.trim() || null,
      model_override: modelOverride || null,
      task_type: taskType || null,
      temperature,
      max_tokens: maxTokens,
      max_retries: maxRetries,
      timeout_seconds: timeoutSeconds,
      thinking_level: showThinkingLevel && thinkingLevel ? thinkingLevel : null,
      top_p: topP != null && topP >= 0 && topP <= 1 ? topP : null,
    }
    await updateAgent.mutateAsync({ agentId: agent.agent_id, orgId, data })
    onSaved?.()
    onClose()
  }

  if (!agent) return null

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Editar: ${agent.display_name}`} size="xl">
      <form onSubmit={handleSave} className="space-y-6">
        <div>
          <label className="label">System Prompt</label>
          <textarea
            className="input font-mono text-sm"
            rows={8}
            style={{ resize: 'vertical', minHeight: '160px' }}
            value={systemPrompt}
            onChange={(e) => setSystemPrompt(e.target.value)}
            placeholder="(default del sistema)"
            disabled={busy}
          />
          <p className="text-xs text-slate-400 mt-1 text-right">{systemPrompt.length} caracteres</p>
        </div>

        <div>
          <label className="label">Modelo</label>
          <select
            className="input"
            value={modelOverride ?? ''}
            onChange={(e) => setModelOverride(e.target.value || null)}
            disabled={busy}
          >
            <option value="">Por defecto (routing)</option>
            {textModels.map((m) => (
              <option key={getModelId(m)} value={getModelId(m)}>
                {getModelDisplayName(m)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="label">Task Type</label>
          <select
            className="input"
            value={taskType}
            onChange={(e) => setTaskType(e.target.value)}
            disabled={busy}
          >
            <option value="">—</option>
            {TASK_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="label">Temperatura: {temperature.toFixed(1)}</label>
          <input
            type="range"
            min={0}
            max={2}
            step={0.1}
            value={temperature}
            onChange={(e) => setTemperature(parseFloat(e.target.value))}
            disabled={busy}
            className="mt-1 w-full h-2 rounded-full appearance-none bg-slate-200 accent-slate-600"
          />
          <p className="text-xs text-slate-400 mt-1">Gemini 3+: recomendado 1.0</p>
          <div className="flex justify-between text-xs text-slate-400 mt-0.5">
            <span>0</span>
            <span>2</span>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div>
            <label className="label">Max Tokens</label>
            <input
              type="number"
              className="input"
              min={256}
              max={32768}
              value={maxTokens}
              onChange={(e) => setMaxTokens(Number(e.target.value))}
              disabled={busy}
            />
          </div>
          <div>
            <label className="label">Max Retries</label>
            <input
              type="number"
              className="input"
              min={0}
              max={10}
              value={maxRetries}
              onChange={(e) => setMaxRetries(Number(e.target.value))}
              disabled={busy}
            />
          </div>
          <div>
            <label className="label">Timeout (s)</label>
            <input
              type="number"
              className="input"
              min={30}
              max={600}
              value={timeoutSeconds}
              onChange={(e) => setTimeoutSeconds(Number(e.target.value))}
              disabled={busy}
            />
          </div>
          {showThinkingLevel && (
            <div>
              <label className="label">Thinking Level</label>
              <select
                className="input"
                value={thinkingLevel}
                onChange={(e) => setThinkingLevel(e.target.value)}
                disabled={busy}
              >
                {THINKING_LEVELS.map((t) => (
                  <option key={t.value || 'none'} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div>
          <label className="label">Top P (opcional, 0–1)</label>
          <input
            type="number"
            className="input"
            min={0}
            max={1}
            step={0.05}
            value={topP ?? ''}
            onChange={(e) => setTopP(e.target.value === '' ? undefined : parseFloat(e.target.value))}
            placeholder="—"
            disabled={busy}
          />
        </div>

        <div className="flex items-center gap-3 pt-4 border-t border-slate-200">
          <button type="submit" disabled={busy} className="btn btn-primary">
            {busy ? 'Guardando...' : 'Guardar'}
          </button>
          <button type="button" onClick={onClose} className="btn btn-secondary">
            Cancelar
          </button>
        </div>
      </form>
    </Modal>
  )
}
